import { useLayoutEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  Box,
  Checkbox,
  Divider,
  Group,
  Input,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { merge } from "lodash";

import { WallService } from "services/wall.service";

import {
  DIAPORAMA_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  DIAPORAMA_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
  ORGANIZATIONS,
} from "_roles";
import { AccessRights } from "common/admin/auth/AccessRights";
import Collapsable from "common/admin/ui/Collapsable";
import { ColorsFormInput } from "common/admin/forms/ColorsFormInput";
import ImagesForm from "common/admin/forms/ImagesForm";
import QueryWrapper from "common/components/QueryWrapper";
// import { MessagesList } from "wall/admin/wall/messages/MessagesList";
import { useGetWallQuery } from "wall/api/walls.endpoints";
import type { Wall } from "wall/types/wall.model";
import { wallTemplate } from "wall/types/wall.model";
import {
  defaultWall,
  useWallForm,
  WallFormProvider,
} from "wall/admin/wall/wallForm";
import { WallBreadcrumbs } from "wall/admin/components/WallBreadcrumbs";
import { WallToolbar } from "wall/admin/components/WallToolbar";
import { MessageGrid } from "./messages/MessageGrid";

export default function WallView() {
  const [isSlideshowLoading, setIsSlideshowLoading] = useState(false);
  const wallService = new WallService();

  const { wallId } = useParams<{ wallId?: string }>();

  const form = useWallForm({
    initialValues: defaultWall,
  });
  const setFormValues = form.setValues;

  const getWallQuery = useGetWallQuery(wallId ?? skipToken, {});

  useLayoutEffect(() => {
    getWallQuery.data &&
      setFormValues(merge({}, defaultWall, getWallQuery.data));
  }, [getWallQuery.data, setFormValues]);

  const location = useLocation();

  useLayoutEffect(() => {
    const state = location.state as { from?: Wall } | undefined;
    if (state?.from) {
      setFormValues(state.from);
      window.history.replaceState(null, "");
    }
  }, [location, setFormValues]);

  const handleTogglePlayPause = async () => {
    console.log("Play/Pause button clicked");
    console.log("Current wall state:", form.values);

    setIsSlideshowLoading(true);

    try {
      const response = await wallService.updateWallStatus({
        wallId: wallId!,
      });
      // Parse the response as JSON to get the updated wall object
      const updatedWall = JSON.parse(response);
      // Update the form with the response from the server to reflect the actual current status
      if (updatedWall && updatedWall.slideshow) {
        form.setFieldValue("slideshow.status", updatedWall.slideshow.status);
      }
      // Return the updated wall to be used by the MessageGrid component
      return updatedWall;
    } catch (error) {
      console.log("Error updating wall status", error);
      throw error;
    } finally {
      setIsSlideshowLoading(false);
      console.log("Update wall status operation completed");
    }
  };

  return (
    <Box>
      <QueryWrapper query={getWallQuery}>
        {(wall) => (
          <WallFormProvider form={form}>
            <div
              className="sticky top-0 z-50 pb-4"
              style={{ backgroundColor: "#E9ECEF" }}
            >
              <Title order={3} align={"center"} m={"xs"}>
                {wallId
                  ? wall?.name || `Part #${wall?.id?.split("-")[0]}`
                  : "New Slideshow"}
              </Title>
              <WallBreadcrumbs wall={wall} />

              <WallToolbar />
            </div>
            <Stack>
              <Group className="grid grid-cols-2">
                <AccessRights
                  authorizedOrganizations={
                    DIAPORAMA_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                  }
                  authorizedRoles={
                    DIAPORAMA_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                  }
                >
                  <Select
                    sx={{ flex: 1 }}
                    label={"Owner"}
                    data={ORGANIZATIONS}
                    {...form.getInputProps("organization")}
                  />
                </AccessRights>
              </Group>
              <Group className="grid grid-cols-2">
                <TextInput
                  autoFocus={(location.state as any)?.from}
                  required
                  sx={{ flex: 1 }}
                  label={"Name"}
                  {...form.getInputProps("name")}
                />
              </Group>

              <Collapsable title="Message mode">
                <Stack>
                  <Input.Wrapper label="Message Mode">
                    <div>
                      <SegmentedControl
                        className="w-full"
                        data={[
                          { value: "TEXT", label: "Text" },
                          { value: "HYBRID", label: "Text + image" },
                          { value: "IMAGES", label: "Image" },
                        ]}
                        {...form.getInputProps("type")}
                      />
                    </div>
                  </Input.Wrapper>
                  <Radio.Group
                    value={form.values.landingMode}
                    label="Landing Mode"
                    onChange={(value) =>
                      form.setFieldValue(
                        "landingMode",
                        value as "message" | "photo"
                      )
                    }
                  >
                    <Radio value="message" label="Message" />
                    <Radio value="photo" label="Photo" />
                  </Radio.Group>
                  <Radio.Group
                    value={form.values.template}
                    label="Screen format"
                    onChange={(value) =>
                      form.setFieldValue("template", value as wallTemplate)
                    }
                  >
                    {Object.entries(wallTemplate).map(([name, type], idx) => (
                      <Radio key={idx} value={type} label={name} />
                    ))}
                  </Radio.Group>
                  <Radio.Group
                    value={form.values.slideShowStructure}
                    label="Slideshow Structure"
                    onChange={(value) =>
                      form.setFieldValue(
                        "slideShowStructure",
                        value as "left-photo" | "right-photo"
                      )
                    }
                  >
                    <Radio value="left-photo" label="Left Photo" />
                    <Radio value="right-photo" label="Right Photo" />
                  </Radio.Group>

                  <TextInput
                    sx={{ flex: 1 }}
                    label={"Slideshow Interval"}
                    placeholder="5000"
                    {...form.getInputProps("slideshow.slideshowInterval")}
                  />
                </Stack>
              </Collapsable>
              {wallId && getWallQuery.currentData && (
                <Collapsable title={`Messages`}>
                  <MessageGrid
                    wallId={wallId}
                    handleTogglePlayPause={handleTogglePlayPause}
                    isSlideshowLoading={isSlideshowLoading}
                    form={form}
                  />
                </Collapsable>
              )}

              <Collapsable title="Texts">
                <Group className="grid grid-cols-2">
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Message title label"}
                    placeholder="Message"
                    {...form.getInputProps("labelFormMessage")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Confirmation button label"}
                    placeholder="Send a new message"
                    {...form.getInputProps("sentButtonLabel")}
                  />
                  <Textarea
                    label="Welcome HTML Code"
                    placeholder="Spectator form logo (mobile)"
                    {...form.getInputProps("images.formLogo")}
                  />
                  <Textarea
                    key="welcome-msg"
                    label="Footer Message"
                    name="welcomeMsg"
                    placeholder="Your footer message"
                    {...form.getInputProps("welcomeMsg")}
                  />
                  <Textarea
                    key="welcome-msg"
                    label="Submission confirmation message"
                    name="welcomeMsg"
                    placeholder="Your message has been successfully sent"
                    {...form.getInputProps("sentMessage")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Page Title"}
                    placeholder="Sign Up"
                    {...form.getInputProps("pageTitle")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"User Name Label"}
                    placeholder="User Name Label"
                    {...form.getInputProps("labels.usernameLabel")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Camera Capture Button Label"}
                    placeholder="Photo"
                    {...form.getInputProps("labels.photoLabel")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Add a Photo Label"}
                    placeholder="Add a Photo Label"
                    {...form.getInputProps("labels.addAPhotoLabel")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Take a Photo Label"}
                    placeholder="Take a Photo Label"
                    {...form.getInputProps("labels.takeAPhotoLabel")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Cancellation Label"}
                    placeholder="Cancellation Label"
                    {...form.getInputProps("labels.cancellationLabel")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Submit Button Label"}
                    placeholder="Photo"
                    {...form.getInputProps("labels.submitLabel")}
                  />
                </Group>
              </Collapsable>

              <Collapsable title={"Font"}>
                <Group className="grid grid-cols-2">
                  <Group>
                    <TextInput
                      className="w-44"
                      required
                      label={"Messages font size"}
                      placeholder="100px"
                      {...form.getInputProps("msgFontSize")}
                    />
                    <TextInput
                      className="w-44"
                      required
                      label={"Messages line height"}
                      placeholder="100px"
                      {...form.getInputProps("msgLineHeight")}
                    />
                    <Group>
                      <Checkbox
                        label="Message Bold"
                        checked={form.values.isMsgBold || false}
                        onChange={(event) =>
                          form.setFieldValue(
                            "isMsgBold",
                            event.currentTarget.checked
                          )
                        }
                      />
                      <Checkbox
                        label="Message Uppercase"
                        checked={form.values.isMsgUppercase || false}
                        onChange={(event) =>
                          form.setFieldValue(
                            "isMsgUppercase",
                            event.currentTarget.checked
                          )
                        }
                      />
                    </Group>
                  </Group>
                  <Group>
                    <TextInput
                      required
                      label={"Author font size"}
                      placeholder="80px"
                      className="w-44"
                      {...form.getInputProps("msgAuthorFontSize")}
                    />
                    <Group>
                      <Checkbox
                        label="Author Bold"
                        checked={form.values.isAuthorBold || false}
                        onChange={(event) =>
                          form.setFieldValue(
                            "isAuthorBold",
                            event.currentTarget.checked
                          )
                        }
                      />
                      <Checkbox
                        label="Author Uppercase"
                        checked={form.values.isAuthorUppercase || false}
                        onChange={(event) =>
                          form.setFieldValue(
                            "isAuthorUppercase",
                            event.currentTarget.checked
                          )
                        }
                      />
                    </Group>
                  </Group>
                </Group>
              </Collapsable>

              <Collapsable title={"Colors"}>
                <Box>
                  <Title order={4}>Sign Up Form</Title>
                  <ColorsFormInput
                    form={form}
                    fields={[
                      //["background", "Fond de page"],
                      ["labels", "Labels"],
                      ["info", "Welcome message"],
                      ["mandatory", "Asterisk"],
                      ["buttonBackground", "Button background"],
                      ["buttonBorder", "Button border"],
                      ["buttonText", "Button label"],
                      ["upload", "Upload label"],
                    ]}
                  />
                </Box>
                <br />
                <Divider my="sm" />
                <br />
                <Box>
                  <Title order={4}>Diaporama</Title>
                  <ColorsFormInput
                    form={form}
                    fields={[
                      ["messages", "Messages"],
                      ["authors", "Authors"],
                    ]}
                  />
                </Box>
              </Collapsable>

              <Collapsable title={"Images"}>
                <ImagesForm
                  form={form}
                  fields={[
                    ["screenBackground", "Background (screen)"],
                    ["playBackground", "Background (mobile)"],
                    ["confirmLogo", "Confirm Logo (mobile)"],
                  ]}
                />
              </Collapsable>
            </Stack>
          </WallFormProvider>
        )}
      </QueryWrapper>
    </Box>
  );
}
